import { LE } from "@/constants/enums";
import { getElectronicFormById } from "@/services/endpoints";
import { useEntityAuditStore } from "@modernary/mui/components/audit";
import { useApi } from "@modernary/mui/utils/api";
import { withAuthorization } from "@modernary/mui/utils/authorization";
import { definePrivateState, generateComputedResource } from "@modernary/mui/utils/helpers";
import { acceptHMRUpdate } from "pinia";
import { computed, ref, shallowRef } from "vue";
import type { ElectronicFormRequest } from "../electronicFormRequest/electronicFormRequest";
import electronicFormRequestService from "../electronicFormRequest/electronicFormRequestService";
import type { ElectronicForm, ShallowElectronicForm } from "./electronicForm";
import electronicFormService from "./electronicFormService";

type ElectronicFormFillInput = ElectronicForm | Promise<ElectronicForm | undefined | null>;

function normalizeFillId(id: string | number | string[]): string | number | undefined {
  const resolved = Array.isArray(id) ? id[0] : id;
  if (resolved === undefined || resolved === "") return undefined;
  return resolved;
}

function isPromiseLike<T>(value: unknown): value is Promise<T> {
  return (
    typeof value === "object" && value !== null && "then" in value && typeof (value as Promise<T>).then === "function"
  );
}

/**
 * Store for the active electronic form context.
 *
 * Public actions:
 * - `fill`: Hydrates store state from a resolved entity or an entity promise.
 * - `fillSelf`: Fetches and hydrates by id (or current form id when omitted).
 * - `reset`: Clears entity state and cancels in-flight hydration work.
 */
export const useElectronicFormStore = definePrivateState(
  "electronicFormStore",
  () => ({
    fillGeneration: 0,
    isLoading: false,
    abortInFlightFetch: null as (() => void) | null,
  }),
  (privateState) => {
    const entityAuditStore = useEntityAuditStore();

    const { exec: execFetchElectronicForm } = withAuthorization(
      useApi(getElectronicFormById, {
        responseAdapter: async (response) => electronicFormService.responseAdapter(response),
      }),
    );

    // state

    const electronicForm = ref<ElectronicForm | null>(null);
    /** Shallow parent form object. */
    const shallowParentForm = shallowRef<ShallowElectronicForm | null>(null);

    // getters

    const authResource = generateComputedResource(electronicForm, computed);
    /** Loading state. True when an in-flight fetch is active. */
    const isLoading = computed(() => privateState.isLoading);
    /** Computed resource for the current repudiation request. */
    const currentWithdrawalRequest = computed(() => {
      const result = electronicForm.value?.electronicFormRequests?.find(
        (request) =>
          request.typeMurn === LE.ElectronicFormRequestType.Withdrawal &&
          request.statusMurn === LE.ElectronicFormRequestStatus.Pending,
      );
      if (result && !result._authRequestFields) {
        electronicFormRequestService.addAuthRequestFields(result as ElectronicFormRequest);
      }
      return result;
    });
    const currentWithdrawalRequestAuthResource = generateComputedResource(currentWithdrawalRequest, computed);

    /** True when the current electronic form is not a draft or submission pending. */
    const isNotDraftOrPending = computed(() => {
      return (
        electronicForm.value?.statusMurn !== LE.ElectronicFormStatus.Draft &&
        electronicForm.value?.statusMurn !== LE.ElectronicFormStatus.SubmissionPending
      );
    });

    // actions
    /**
     * @private
     * Applies a fully-resolved electronic form entity to store state.
     */
    function applyFill(entity: ElectronicForm) {
      electronicForm.value = entity;
      shallowParentForm.value = entity.parent ?? null;
      entityAuditStore.fill(entity, {
        entityType: "electronicForm",
        entityId: entity.id,
        statusLookupGroupCode: "ElectronicFormStatus",
      });
    }

    /**
     * @private
     * Cancels the current fetch request if one is active.
     */
    function abortPendingFetch() {
      privateState.abortInFlightFetch?.();
      privateState.abortInFlightFetch = null;
    }

    /**
     * @private
     * Invalidates in-flight hydration work and clears loading state.
     */
    function supersedeInFlightFetch() {
      ++privateState.fillGeneration;
      abortPendingFetch();
      privateState.isLoading = false;
    }

    /**
     * Resets the electronic form state and audit context.
     */
    function reset() {
      supersedeInFlightFetch();
      electronicForm.value = null;
      shallowParentForm.value = null;
      entityAuditStore.reset();
    }

    /**
     * Hydrates store state from either:
     * - a resolved `ElectronicForm`, or
     * - a promise that resolves to an `ElectronicForm`.
     *
     * Promise hydration is guarded against stale completion using a generation token.
     */
    async function fill(input: ElectronicFormFillInput): Promise<void> {
      if (isPromiseLike<ElectronicForm | undefined | null>(input)) {
        const generation = ++privateState.fillGeneration;
        abortPendingFetch();
        privateState.isLoading = true;

        try {
          const entity = await input;
          if (!entity || generation !== privateState.fillGeneration) return;
          applyFill(entity);
        } finally {
          if (generation === privateState.fillGeneration) {
            privateState.isLoading = false;
          }
        }
        return;
      }

      if (!input) return;
      supersedeInFlightFetch();
      applyFill(input);
    }

    /**
     * Fetches and hydrates the current electronic form by id.
     * If `id` is omitted, the current `electronicForm.id` is used.
     */
    async function fillSelf(id?: string | number | string[]): Promise<void> {
      const resolvedId = id !== undefined ? normalizeFillId(id) : electronicForm.value?.id;
      if (resolvedId === undefined) return;

      const generation = ++privateState.fillGeneration;
      abortPendingFetch();
      privateState.isLoading = true;

      try {
        const entity = await execFetchElectronicForm(resolvedId, {
          abortMethod: (abort: () => void) => {
            privateState.abortInFlightFetch = abort;
          },
        });
        if (!entity || generation !== privateState.fillGeneration) return;
        applyFill(entity);
        return;
      } finally {
        if (generation === privateState.fillGeneration) {
          privateState.isLoading = false;
        }
      }
    }

    return {
      // state
      electronicForm,
      shallowParentForm,
      // getters
      authResource,
      isLoading,
      currentWithdrawalRequest,
      currentWithdrawalRequestAuthResource,
      isNotDraftOrPending,
      // actions
      applyFill,
      abortPendingFetch,
      supersedeInFlightFetch,
      reset,
      fill,
      fillSelf,
    };
  },
);

export type UseElectronicFormStore = ReturnType<typeof useElectronicFormStore>;

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useElectronicFormStore, import.meta.hot));
}
